import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MISSION_ENTRY_SOURCE,
  MISSION_ERROR,
  missionErrorCode,
  startMissionEvolution,
} from "../../../Services/journey";
import { trackEvent } from "../../../Services/amplitude";
import keyboardIcon from "../../../Assets/icon/explore/keyboard-teal.svg";
import { ChevronIcon, JourneyBackHeader, JourneySubpageCard, useStandaloneHeader } from "../shared";
import { MISSION_EVENT, missionRoutes } from "./evolution";
import { EVOLVE_COPY as C } from "./copy";
import "../style.css";
import "./style.css";

/**
 * Evolve your Mission (Figma 11268:27264) —
 * `/journey/mission/evolve?entry_source=&recommendation_id=`.
 *
 * Nothing is created until the member picks Voice or Text: Back from here
 * calls nothing. The pick calls `POST /journey/mission/evolution` (start or
 * resume — one open evolution per member, so a double tap yields the same
 * session) and opens the existing Mission conversation with the evolution id.
 */

const ENTRY_SOURCES = new Set(Object.values(MISSION_ENTRY_SOURCE));

/** Where the conversation opens, from the start response's `launch` block. */
export const conversationRoute = (mode, started) => {
  const id = started?.evolution_session_id;
  if (!id) return null;
  if (mode === "VOICE") {
    const query = new URLSearchParams({
      dialog_type: "mission_evolution",
      ...(started?.launch?.voice?.query || {}),
      evolution_session_id: id,
    });
    return `/coach/private?${query.toString()}`;
  }
  return `/chat?module=MISSION_EVOLUTION&evolutionSessionId=${encodeURIComponent(id)}`;
};

const CompassIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <circle cx="24" cy="24" r="19" stroke="#fff" strokeWidth="3" />
    <path d="M30.5 17.5l-4 9-9 4 4-9 9-4z" stroke="#fff" strokeWidth="2.5" strokeLinejoin="round" />
  </svg>
);

const VoiceBarsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    {[[4, 11, 17], [8.5, 8, 20], [13, 4, 24], [17.5, 8, 20], [22, 11, 17]].map(([x, y1, y2]) => (
      <path key={x} d={`M${x + 1} ${y1}V${y2}`} stroke="#0d70dc" strokeWidth="2.4" strokeLinecap="round" />
    ))}
  </svg>
);

const InfoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#0d70dc" />
    <path d="M12 11v6M12 7.5v.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function EvolveMission() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useStandaloneHeader();

  const rawSource = searchParams.get("entry_source");
  const entrySource = ENTRY_SOURCES.has(rawSource) ? rawSource : MISSION_ENTRY_SOURCE.JOURNEY_MISSION;
  const recommendationId = searchParams.get("recommendation_id") || null;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const busyRef = useRef(false);

  useEffect(() => {
    trackEvent(MISSION_EVENT.EVOLVE_OPENED, { entry_source: entrySource });
  }, [entrySource]);

  const choose = async (mode) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    trackEvent(MISSION_EVENT.MODE_SELECTED, { mode, entry_source: entrySource });
    try {
      const started = await startMissionEvolution({ mode, entrySource, recommendationId });
      if (started && started.resumed === false) {
        trackEvent(MISSION_EVENT.EVOLUTION_STARTED, { mode, entry_source: entrySource });
      }
      const route = conversationRoute(mode, started);
      if (!route) throw new Error("start returned no evolution_session_id");
      navigate(route);
    } catch (e) {
      console.error("Failed to start the mission evolution", e);
      setError(missionErrorCode(e) === MISSION_ERROR.NO_ACTIVE_MISSION ? C.noMission : C.startError);
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to={missionRoutes.mission} title={C.title} />
        <JourneySubpageCard>
          <section className="journey-evolve__hero">
            <CompassIcon />
            <h2 className="journey-evolve__hero-title">{C.heroTitle}</h2>
            <p className="journey-evolve__hero-body">{C.heroBody}</p>
            <span className="journey-mission-card__orb" aria-hidden="true" />
          </section>

          <section className="journey-evolve__choose">
            <h2 className="journey-evolve__question">{C.question}</h2>
            <div className="journey-rows">
              <button
                type="button"
                className="journey-row journey-row--link"
                disabled={busy}
                onClick={() => choose("VOICE")}
              >
                <span className="journey-evolve__tile journey-evolve__tile--voice">
                  <VoiceBarsIcon />
                </span>
                <span className="journey-row__text">
                  <span className="journey-row__title">{C.voice}</span>
                  <span className="journey-row__sub">{C.voiceHint}</span>
                </span>
                <span className="journey-row__chevron">
                  <ChevronIcon />
                </span>
              </button>
              <button
                type="button"
                className="journey-row journey-row--link"
                disabled={busy}
                onClick={() => choose("TEXT")}
              >
                <span className="journey-evolve__tile journey-evolve__tile--text">
                  <img src={keyboardIcon} alt="" width="28" height="28" />
                </span>
                <span className="journey-row__text">
                  <span className="journey-row__title">{C.text}</span>
                  <span className="journey-row__sub">{C.textHint}</span>
                </span>
                <span className="journey-row__chevron">
                  <ChevronIcon />
                </span>
              </button>
            </div>
            {error && (
              <p className="journey-evolve__error" role="alert">
                {error}
              </p>
            )}
          </section>

          <section className="journey-mission__info">
            <InfoIcon />
            <div>
              <h3 className="journey-mission__info-title">{C.expectTitle}</h3>
              <p className="journey-mission__info-body">{C.expectBody}</p>
            </div>
          </section>
        </JourneySubpageCard>
      </div>
    </div>
  );
}

export default EvolveMission;
